import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  TextField,
  Paper,
  Stack,
  IconButton,
  Fade,
  useTheme,
  alpha,
  CircularProgress,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  CloudUpload,
  Add as AddIcon,
  Link as LinkIcon,
  Save as SaveIcon,
  Info,
  Palette as PaletteIcon,
  LightMode,
  DarkMode,
  CheckCircle,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useColorMode } from '../../../theme/ThemeContext';
import { useAuth } from '../../../utils/AuthContext';

const GOLD = '#D4AF37';

// ─── Theme Preview Card ───────────────────────────────────────────────────────
const ThemeCard = ({ themeMode, label, description, icon, isSelected, onClick }) => {
  const isLight = themeMode === 'light';
  const cardBg = isLight ? '#FFFFFF' : '#1A1A1A';
  const textColor = isLight ? '#1A1A1A' : '#FFFFFF';
  const subTextColor = isLight ? '#666' : '#aaa';

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        borderRadius: '20px',
        border: isSelected ? `2.5px solid ${GOLD}` : '2.5px solid transparent',
        boxShadow: isSelected
          ? `0 0 0 4px ${alpha(GOLD, 0.18)}, 0 8px 30px ${alpha(GOLD, 0.12)}`
          : '0 4px 20px rgba(0,0,0,0.08)',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 0 0 3px ${alpha(GOLD, 0.15)}, 0 12px 35px rgba(0,0,0,0.12)`,
        },
      }}
    >
      <Box sx={{ bgcolor: cardBg, p: 2, height: 160, display: 'flex', flexDirection: 'column', gap: 1, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 48, height: 8, borderRadius: 4, bgcolor: GOLD }} />
          <Box sx={{ flex: 1 }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: GOLD }} />
          <Box sx={{ width: 20, height: 8, borderRadius: 4, bgcolor: isLight ? '#ddd' : '#444' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
          <Box sx={{ width: 36, borderRadius: 2, bgcolor: isLight ? '#F0F0F0' : '#2A2A2A', p: 0.5 }}>
            {[...Array(4)].map((_, i) => (
              <Box key={i} sx={{ height: 5, borderRadius: 2, mb: 0.6, bgcolor: i === 0 ? GOLD : isLight ? '#DDD' : '#444' }} />
            ))}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ height: 10, borderRadius: 2, mb: 1, bgcolor: isLight ? '#E8E8E8' : '#333', width: '70%' }} />
            <Box sx={{ display: 'flex', gap: 0.8 }}>
              {[...Array(3)].map((_, i) => (
                <Box key={i} sx={{ flex: 1, height: 40, borderRadius: 2, bgcolor: isLight ? '#F5F5F5' : '#252525', border: `1px solid ${isLight ? '#E0E0E0' : '#3A3A3A'}` }} />
              ))}
            </Box>
          </Box>
        </Box>
        {isSelected && (
          <Box sx={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${alpha(GOLD, 0.07)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
        )}
      </Box>

      <Box
        sx={{
          p: 2,
          bgcolor: isSelected ? alpha(GOLD, 0.05) : (isLight ? '#FAFAFA' : '#111'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: `1px solid ${isLight ? '#F0F0F0' : '#2A2A2A'}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ color: GOLD, display: 'flex', alignItems: 'center' }}>{icon}</Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: textColor, lineHeight: 1.2 }}>{label}</Typography>
            <Typography variant="caption" sx={{ color: subTextColor }}>{description}</Typography>
          </Box>
        </Box>
        {isSelected && <CheckCircle sx={{ color: GOLD, fontSize: 22 }} />}
      </Box>
    </Box>
  );
};

// ─── Main Admin Setting Component ─────────────────────────────────────────────
const AdminSetting = () => {
  const theme = useTheme();
  const { mode, toggleColorMode } = useColorMode();

  const [profileSettings, setProfileSettings] = useState({
    bio: '',
    tagline: '',
    profile_pic: '',
    social_links: [],
  });

  const [profilePreview, setProfilePreview] = useState('');
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('jwttoken');

  const { userData, refreshUserData } = useAuth();

  useEffect(() => {
    if (userData) {
      setProfileSettings({
        bio: userData.bio || '',
        tagline: userData.tagline || '',
        profile_pic: userData.profile_pic || '',
        social_links: userData.social_links || [],
      });
      setProfilePreview(userData.profile_pic || '');
      setLoading(false);
    }
  }, [userData]);

  const handleChange = (e) => {
    setProfileSettings({ ...profileSettings, [e.target.name]: e.target.value });
  };

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result);
        setProfileSettings({ ...profileSettings, profile_pic: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.patch('https://tdtlworld.com/dss-kaas-backend/lms/user/', profileSettings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      refreshUserData(); // Refresh global admin state
      Swal.fire({ icon: 'success', title: 'Updated!', text: 'Admin settings saved successfully.', confirmButtonColor: GOLD });
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error!', text: 'Failed to update settings.', confirmButtonColor: GOLD });
    }
  };

  const handleThemeSelect = (selectedMode) => {
    if (selectedMode !== mode) toggleColorMode();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 2, color: theme.palette.mode === 'dark' ? '#FFFFFF' : 'text.primary' }}>
          <SettingsIcon sx={{ fontSize: '2.5rem', color: GOLD }} /> Admin Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure your administrative profile and portal appearance.
        </Typography>
      </Box>

      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <PaletteIcon sx={{ color: GOLD, fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={800}>Appearance &amp; Theme</Typography>
            <Typography variant="body2" color="text.secondary">Select your preferred visual style for the dashboard.</Typography>
          </Box>
          <Chip
            label={mode === 'dark' ? 'Dark Active' : 'Light Active'}
            size="small"
            sx={{ ml: 'auto', fontWeight: 700, bgcolor: alpha(GOLD, 0.12), color: GOLD }}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <ThemeCard
              themeMode="light"
              label="Light Mode"
              description="Clean & bright interface"
              icon={<LightMode />}
              isSelected={mode === 'light'}
              onClick={() => handleThemeSelect('light')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ThemeCard
              themeMode="dark"
              label="Dark Mode"
              description="Sleek dark interface"
              icon={<DarkMode />}
              isSelected={mode === 'dark'}
              onClick={() => handleThemeSelect('dark')}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <SettingsIcon sx={{ color: GOLD, fontSize: 28 }} />
        <Box>
          <Typography variant="h6" fontWeight={800}>Admin Profile Details</Typography>
          <Typography variant="body2" color="text.secondary">Manage your public information and tagline.</Typography>
        </Box>
      </Box>

      <form onSubmit={handleSave}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: '24px', border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Avatar
                      src={profilePreview}
                      sx={{ width: 120, height: 120, mx: 'auto', mb: 2, border: '4px solid', borderColor: GOLD, boxShadow: theme.shadows[4] }}
                    />
                    <IconButton
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ position: 'absolute', bottom: 10, right: 0, bgcolor: GOLD, color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                    >
                      <CloudUpload fontSize="small" />
                    </IconButton>
                  </Box>
                  <input type='file' ref={fileInputRef} className='d-none' onChange={handleProfilePicChange} accept='image/*' />
                </Box>
                <Stack spacing={3}>
                  <TextField fullWidth label="Admin Tagline" name="tagline" value={profileSettings.tagline} onChange={handleChange} variant="outlined" />
                  <TextField fullWidth multiline rows={4} label="Bio" name="bio" value={profileSettings.bio} onChange={handleChange} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card sx={{ borderRadius: '24px', border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <LinkIcon color="primary" />
                  <Typography variant="h6" fontWeight={800}>Community Links</Typography>
                </Box>
                <Stack spacing={2} sx={{ mb: 3 }}>
                  {profileSettings.social_links.map((link, index) => (
                    <Fade in={true} key={index}>
                      <TextField
                        fullWidth
                        label={`Link ${index + 1}`}
                        value={link}
                        onChange={(e) => {
                          const newLinks = [...profileSettings.social_links];
                          newLinks[index] = e.target.value;
                          setProfileSettings({ ...profileSettings, social_links: newLinks });
                        }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><LinkIcon fontSize="small" /></InputAdornment> }}
                      />
                    </Fade>
                  ))}
                </Stack>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setProfileSettings({ ...profileSettings, social_links: [...profileSettings.social_links, ''] })}
                  sx={{ borderRadius: '50px', fontWeight: 700 }}
                >
                  Add Social Link
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sx={{ mt: 2 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3, borderRadius: '20px',
                bgcolor: alpha(GOLD, 0.05),
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                border: '1px solid', borderColor: alpha(GOLD, 0.2),
              }}
            >
              <Typography variant="body2" fontWeight={600}>Save your changes to update your admin profile across the portal.</Typography>
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                sx={{ px: 6, borderRadius: '50px', fontWeight: 800, bgcolor: GOLD }}
              >
                Save Settings
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default AdminSetting;
